// Smart Scout (Coach's Eye) payload builder — extracted verbatim from
// StatsView (2026-07) so the Season Dashboard passes IDENTICAL data to
// CoachsEyeModal. The localStorage cache key convention
// (`${team.id}_${gameSetHash}`) moves with it.
//
// Pure + framework-free.

import type { StreakSignal } from './streak-signals';

export interface TeamPitchingAggregates {
  teamERA: number | null;
  teamFIP: number | null;
  teamBABIP: number | null;
  teamDSSResult: any | null;
  tIP: number;
}

export function buildCoachsEyePayload(args: {
  team: any;
  players: any[];
  games: any[];
  stats: Record<string, any>;
  streakSignals: StreakSignal[];
  teamPitching: TeamPitchingAggregates;
}) {
  const { team, players, games, stats, streakSignals, teamPitching } = args;
  const { teamERA, teamFIP, teamBABIP, teamDSSResult, tIP } = teamPitching;

  const completedGames = games
    .filter(g => g.status === 'completed' && g.stats)
    .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
  const gameCount = completedGames.length;
  const last3Games = completedGames.slice(-3);

  // Build hot/cold signal map from streakSignals
  const hotColdMap: Record<string, { hot: string[]; cold: string[] }> = {};
  streakSignals.forEach(sig => {
    if (!hotColdMap[sig.playerId]) hotColdMap[sig.playerId] = { hot: [], cold: [] };
    if (sig.direction === 'hot') hotColdMap[sig.playerId].hot.push(sig.label);
    else hotColdMap[sig.playerId].cold.push(sig.label);
  });

  // Team batting aggregates
  let tPA = 0, tH = 0, tBB = 0, tSO = 0, tAB = 0;
  let sumObp = 0, wObp = 0, sumOps = 0, wOps = 0;
  Object.values(stats).forEach((s: any) => {
    tPA += s.batting.pa; tH += s.batting.h; tBB += s.batting.bb;
    tSO += s.batting.so; tAB += s.batting.ab;
    if (s.batting.total_obp_weight > 0) { sumObp += s.batting.sum_obp; wObp += s.batting.total_obp_weight; }
    if (s.batting.total_ops_weight > 0) { sumOps += s.batting.sum_ops; wOps += s.batting.total_ops_weight; }
  });

  const last5Games = completedGames.slice(-5);

  // ── Team results context — ALL completed games with a recorded result
  //    (a result doesn't require imported stats, so this set can be larger) ──
  const resultGames = games
    .filter(g => g.status === 'completed' && g.result)
    .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
  const rW = resultGames.filter(g => g.result.outcome === 'W').length;
  const rL = resultGames.filter(g => g.result.outcome === 'L').length;
  const rT = resultGames.filter(g => g.result.outcome === 'T').length;
  const oneRunGames = resultGames.filter(g => Math.abs((g.result.myScore ?? 0) - (g.result.opponentScore ?? 0)) === 1);
  const last5Results = resultGames.slice(-5);
  const teamResults = resultGames.length > 0 ? {
    wins: rW, losses: rL, ties: rT,
    runDiff: resultGames.reduce((s, g) => s + ((g.result.myScore ?? 0) - (g.result.opponentScore ?? 0)), 0),
    oneRunRecord: `${oneRunGames.filter(g => g.result.outcome === 'W').length}-${oneRunGames.filter(g => g.result.outcome === 'L').length}`,
    last5Record: `${last5Results.filter(g => g.result.outcome === 'W').length}-${last5Results.filter(g => g.result.outcome === 'L').length}`,
  } : null;

  // ── Team recent window — the same last-3-games treatment players get ──
  const teamRecent = (() => {
    if (last3Games.length === 0) return null;
    let pa = 0, ab = 0, h = 0, bb = 0, so = 0, ip = 0, er = 0;
    let rf = 0, ra = 0, withResult = 0;
    last3Games.forEach(g => {
      Object.values(g.stats || {}).forEach((gs: any) => {
        pa += gs.pa || 0; ab += gs.ab || 0; h += gs.h || 0; bb += gs.bb || 0; so += gs.so || 0;
        ip += gs.ip || 0; er += gs.erAllowed ?? gs.er ?? 0;
      });
      if (g.result) { rf += g.result.myScore ?? 0; ra += g.result.opponentScore ?? 0; withResult++; }
    });
    return {
      games: last3Games.length,
      avg: ab > 0 ? h / ab : null,
      obp: pa > 0 ? (h + bb) / pa : null,
      kRate: ab > 0 ? so / ab : null,
      bbRate: pa > 0 ? bb / pa : null,
      era: ip > 0 ? (er / ip) * 9 : null,
      runsForPerGame: withResult > 0 ? rf / withResult : null,
      runsAgainstPerGame: withResult > 0 ? ra / withResult : null,
    };
  })();

  // ── Team game arc — last 6 results, newest first ──
  const teamGameArc = [...resultGames].slice(-6).reverse().map(g => ({
    date: g.date || '',
    opponent: g.opponent || '?',
    outcome: g.result.outcome,
    myScore: g.result.myScore ?? null,
    opponentScore: g.result.opponentScore ?? null,
  }));

  const playerPayloads = players.map(p => {
    const s = stats[p.id];
    const b = s?.batting;
    const pit = s?.pitching;
    const hc = hotColdMap[p.id] || { hot: [], cold: [] };

    // Last-3 game stats per player
    let l3pa = 0, l3ab = 0, l3h = 0, l3bb = 0, l3so = 0, l3rbi = 0;
    let l3ip = 0, l3er = 0, l3sPctSum = 0, l3sPctW = 0, l3pitchGames = 0;
    last3Games.forEach(g => {
      const gs = g.stats?.[p.id];
      if (!gs) return;
      if (gs.pa > 0 || gs.ab > 0) {
        l3pa += gs.pa || 0; l3ab += gs.ab || 0; l3h += gs.h || 0;
        l3bb += gs.bb || 0; l3so += gs.so || 0; l3rbi += gs.rbi || 0;
      }
      if ((gs.ip || 0) > 0) {
        l3ip += gs.ip || 0; l3er += gs.erAllowed || gs.er || 0;
        const sp = gs.sPct != null ? (gs.sPct > 1 ? gs.sPct / 100 : gs.sPct) : null;
        if (sp != null) { l3sPctSum += sp * (gs.pitches || 1); l3sPctW += gs.pitches || 1; }
        l3pitchGames++;
      }
    });

    const recent = {
      batting: l3pa > 0 ? {
        pa: l3pa, h: l3h, bb: l3bb, so: l3so, rbi: l3rbi,
        avg: l3ab > 0 ? l3h / l3ab : null,
        obp: l3pa > 0 ? (l3h + l3bb) / l3pa : null,
      } : null,
      pitching: l3ip > 0 ? {
        ip: l3ip,
        era: l3ip > 0 ? (l3er / l3ip) * 9 : null,
        sPct: l3sPctW > 0 ? l3sPctSum / l3sPctW : null,
        games: l3pitchGames,
      } : null,
    };

    // Per-game arc: last 5 games, newest first
    const gameLog = [...last5Games].reverse().map(g => {
      const gs = g.stats?.[p.id];
      if (!gs) return null;
      const hasBatting = (gs.pa || 0) > 0 || (gs.ab || 0) > 0;
      const hasPitching = (gs.ip || 0) > 0;
      if (!hasBatting && !hasPitching) return null;
      return {
        date: g.date || '',
        batting: hasBatting ? {
          ab: gs.ab || 0, h: gs.h || 0, bb: gs.bb || 0, so: gs.so || 0, rbi: gs.rbi || 0,
        } : undefined,
        pitching: hasPitching ? {
          ip: gs.ip,
          er: gs.erAllowed ?? gs.er ?? 0,
          sPct: gs.sPct != null ? (gs.sPct > 1 ? gs.sPct / 100 : gs.sPct) : undefined,
        } : undefined,
      };
    }).filter(Boolean);

    return {
      id: p.id,
      name: p.name,
      number: p.number || '?',
      primaryPosition: p.primaryPosition || p.positions?.[0] || '?',
      batting: b && b.pa > 0 ? {
        pa: b.pa, ab: b.ab, avg: b.avg ?? null, obp: b.obp ?? null,
        ops: b.ops ?? null, h: b.h, bb: b.bb, so: b.so, rbi: b.rbi,
      } : null,
      pitching: pit && pit.ip > 0 ? {
        ip: pit.ip, era: pit.era ?? null, whip: pit.whip ?? null,
        so: pit.so, bb: pit.bb, sPct: pit.sPct ?? null,
        fpsPct: pit.fpsPct ?? null, fip: pit.fip ?? null,
        babip: pit.babip ?? null, dss: pit.dss ?? null,
      } : null,
      recent,
      gameLog,
      hotSignals: hc.hot,
      coldSignals: hc.cold,
      attributes: p.attributes ?? null,
    };
  });

  const gameSetHash = completedGames.map(g => g.id).sort().join('_');
  const cacheKey = `${team.id}_${gameSetHash}`;

  return {
    teamId: team.id,
    gameSetHash,
    teamInfo: {
      name: team.name || 'Team',
      ageGroup: team.ageGroup || team.settings?.ageGroup || '?',
      styleOfPlay: team.settings?.styleOfPlay || team.styleOfPlay || '?',
    },
    teamStats: {
      era: teamERA,
      fip: teamFIP,
      babip: teamBABIP,
      dss: teamDSSResult,
      ip: tIP,
      obp: wObp > 0 ? sumObp / wObp : null,
      ops: wOps > 0 ? sumOps / wOps : null,
      kRate: tAB > 0 ? tSO / tAB : null,
      bbRate: tPA > 0 ? tBB / tPA : null,
    },
    teamResults,
    teamRecent,
    teamGameArc,
    players: playerPayloads,
    gameCount,
    cacheKey,
    isDemoSession: team.isDemoSession === true,
  };
}
